import nodeFetch, {
  BodyInit as NodeBodyInit,
  Headers as NodeHeaders,
  // renaming type to differentiate from fetch Response
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Response as NodeResponse,
} from 'node-fetch';
import hash from 'object-hash';
import masonErrorHandlers from '@bymason/error-handlers';
import { getHeaders } from './get-headers';

const DEFAULT_THROTTLE_MS = 250;
const throttleMap: { [hash: string]: number } = {};

export type HttpVerb = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface CallEndpointOptions {
  body?: BodyInit;
  cache?: RequestCache;
  cacheKey?: string;
  clientToken?: string;
  correlationId?: string;
  headers?: Headers | NodeHeaders;
  method: HttpVerb;
  mode?: RequestMode;
  throttleMs?: number;
  url: string;
  signal?: AbortSignal | null;
}

export default async function callEndpoint({
  body,
  cache = 'no-cache',
  cacheKey,
  clientToken,
  correlationId,
  headers,
  method,
  mode = 'cors',
  throttleMs = DEFAULT_THROTTLE_MS,
  url,
  signal,
}: CallEndpointOptions) {
  const throttleKey =
    cacheKey ||
    hash(
      { url, body, method },
      {
        ignoreUnknown: true,
      }
    );
  if (throttleMap[throttleKey] && Date.now() - throttleMap[throttleKey] < throttleMs) {
    // elapsed time since last call for this hash has
    // not yet exeeded throttle limit, so ignore call
    return Promise.resolve();
  }

  throttleMap[throttleKey] = Date.now();

  const useNativeFetch = typeof fetch !== 'undefined';

  const hdrs = getHeaders(headers);

  if (clientToken) {
    hdrs.append('Authorization', `Bearer ${clientToken}`);
  }
  if (correlationId) {
    hdrs.append('x-correlation-id', correlationId);
  }

  let resp;

  if (useNativeFetch) {
    resp = await fetch(url, { body, headers: hdrs as Headers, method, mode, cache, signal });
  } else {
    const nodeBody = body as NodeBodyInit;
    resp = (await nodeFetch(url, {
      body: nodeBody,
      headers: hdrs as NodeHeaders,
      method,
    })) as NodeResponse;
  }

  if (masonErrorHandlers.onDataApiAuthError && resp.status === 401) {
    masonErrorHandlers.onDataApiAuthError(method, url);
  }

  if (!resp.ok) {
    // The backend will only send x-mason-debug-url back to the frontend if the user
    // is specified as MasonOps, MasonSupport, or MasonTest as a role in Auth0
    // However, in that case, it always sends the debug url (on success or failure),
    // so we limit logging to cases where there's an error and the header is present
    const debugUrl = resp.headers.get('x-Mason-Debug-Url');
    if (debugUrl) {
      console.log(`Error processing ${url}\n Logs are here: ${debugUrl}`);
    }
  }

  return resp;
}
